import { useEffect } from 'react';
import api from '../lib/api';

/* Runtime SEO wiring that the owner controls from Admin → Site Settings, with no redeploy:
   - entity links (sameAs) on the Organization schema, which is how answer engines resolve
     "who is Axicor" into a real company
   - Google Search Console and Bing Webmaster verification tags
   - GA4, loaded only once an ID is actually configured
   - machine-readable alternates (llms.txt, llms-full.txt) advertised in the head */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://www.axicor.ae';

const meta = (name, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const altLink = (type, href, title) => {
  if (document.querySelector(`link[rel="alternate"][href="${href}"]`)) return;
  const el = document.createElement('link');
  el.setAttribute('rel', 'alternate');
  el.setAttribute('type', type);
  el.setAttribute('href', href);
  el.setAttribute('title', title);
  document.head.appendChild(el);
};

export default function SeoRuntime() {
  useEffect(() => {
    altLink('text/plain', `${SITE_URL}/llms.txt`, 'Site summary for AI answer engines');
    altLink('text/plain', `${BACKEND}/api/llms-full.txt`, 'Full machine-readable catalogue and answers');

    api.get('/settings/public/site_settings').then(({ data }) => {
      const cfg = data || {};
      meta('google-site-verification', cfg.gsc_verification);
      meta('msvalidate.01', cfg.bing_verification);

      const sameAs = [cfg.social_linkedin, cfg.social_twitter, cfg.social_facebook,
                      cfg.social_instagram, cfg.google_business_url]
        .filter(u => typeof u === 'string' && u.startsWith('http'));
      if (sameAs.length) {
        document.querySelectorAll('script[data-seo-sameas]').forEach(el => el.remove());
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-sameas', 'true');
        script.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@id': `${SITE_URL}/#organization`,
          '@type': 'Organization',
          name: 'Axicor DWC LLC',
          url: SITE_URL,
          sameAs,
        });
        document.head.appendChild(script);
      }

      const id = (cfg.ga4_id || '').trim();
      if (id && !window.__ga4Loaded) {
        window.__ga4Loaded = true;
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', id, { anonymize_ip: true });
      }
    }).catch(() => {});
  }, []);

  return null;
}
